import { get } from 'lodash';
import React from 'react';
import { DraggableCore, DraggableData } from 'react-draggable';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { connect } from 'react-redux';

import { ISpace, IShadow } from '../../../../types/space';
import { IDispatch, IRootState } from '../../../../modules/stores';
import { ElementTypes } from '../../../../types/element';
import { fireMouseEvent, getForeignTableIdByType } from '../../../../helpers';

import { SpaceWrapper, SpaceBackConfig, ContainerInner } from '../styled';
import SpaceAbsent from '../Absent';

import { isDisabledOrDeletedSyncTable } from '../../../../modules/core/helpers';

const GRID_SPACE = 10;

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
interface IState {
  resizing: boolean;
}
interface IProps {
  space: ISpace<ElementTypes>;
  shadow: IShadow | null; // We aren't doing anything with this, it is used in view.
}

/**
 * The user is currently editing this space. They have clicked on it and now can move it around,
 * resize, pivot, etc.
 */
class SpaceEdit extends React.PureComponent<IProps & IConnectProps, IState> {
  // private childRef: React.RefObject<HTMLDivElement>;
  private draggableRef: React.RefObject<HTMLDivElement>;

  constructor(props: IProps & IConnectProps) {
    super(props);
    this.draggableRef = React.createRef();

    this.state = {
      resizing: false,
    };
  }

  public onDragHandler = (handlerName: string) => {
    const { space, updateSpace } = this.props;

    return (_: any, { deltaX, deltaY }: DraggableData) => {
      switch (handlerName) {
        case 'onDragStart': {
          break;
        }
        case 'onDrag':
          if (!this.state.resizing) {
            updateSpace({
              id: space.id,
              updates: { posX: space.posX + deltaX, posY: space.posY + deltaY },
            });
          }
          break;
        case 'onDragStop':
          break;
        default:
          throw new Error(`onDragHandler called with unrecognized handlerName: ${handlerName}`);
      }
    };
  };

  public onResizeHandler = (handlerName: string) => {
    const { space, updateSpace } = this.props;

    return (_: any, { size }: ResizeCallbackData) => {
      switch (handlerName) {
        case 'onResizeStart': {
          this.setState({ resizing: true });
          break;
        }
        case 'onResize':
          if (size.width !== space.width) {
            updateSpace({
              id: space.id,
              updates: { width: size.width },
            });
          }

          if (size.height !== space.height) {
            updateSpace({
              id: space.id,
              updates: { height: size.height },
            });
          }
          break;
        case 'onResizeStop':
          this.setState({ resizing: false });
          break;
        default:
          throw new Error(`onDragHandler called with unrecognized handlerName: ${handlerName}`);
      }
    };
  };

  public componentDidMount() {
    this.draggableRef.current && fireMouseEvent('mousedown', this.draggableRef.current);
  }

  public render() {
    const { children, space, fieldAbsent, fieldById, nodes } = this.props;

    let showAbsentSpace = fieldAbsent;

    if (space.fieldId) {
      const field = fieldById[space.fieldId];

      if (field) {
        const foriegnTableId = getForeignTableIdByType(field);

        if (foriegnTableId) {
          showAbsentSpace =
            showAbsentSpace ||
            !nodes[foriegnTableId] ||
            isDisabledOrDeletedSyncTable(nodes[foriegnTableId].syncTableMeta);
        }
      }
    }

    return (
      <DraggableCore
        grid={[GRID_SPACE, GRID_SPACE]}
        onDrag={this.onDragHandler('onDrag')}
        onStart={this.onDragHandler('onDragStart')}
        onStop={this.onDragHandler('onDragStop')}
      >
        <Resizable
          height={space.height}
          onResize={this.onResizeHandler('onResize')}
          onResizeStart={this.onResizeHandler('onResizeStart')}
          onResizeStop={this.onResizeHandler('onResizeStop')}
          width={space.width}
          draggableOpts={{ grid: [GRID_SPACE, GRID_SPACE] }}
        >
          <SpaceWrapper
            config={space.config}
            style={{
              border: `2px solid ${space.isAvoidCollision ? '#25993D' : '#577eff'}`,
              transform: `translate(${space.posX}px, ${space.posY}px) rotate(${space.rotation}deg)`,
              transformOrigin: `0px 0px`,
            }}
          >
            <ContainerInner
              ref={this.draggableRef}
              style={{
                width: `${space.width}px`,
                height: `${space.height}px`,
              }}
            >
              <SpaceBackConfig config={space.config}>
                {showAbsentSpace ? <SpaceAbsent /> : children}
              </SpaceBackConfig>
            </ContainerInner>
          </SpaceWrapper>
        </Resizable>
      </DraggableCore>
    );
  }
}

const mapState = ({ core }: IRootState, { space }: IProps) => {
  return {
    fieldAbsent: !(
      space.type === ElementTypes.StaticAttachment ||
      space.type === ElementTypes.StaticText ||
      get(core, `fieldById[${space.fieldId}]`)
    ),
    nodes: core.visibleNodes,
    fieldById: core.fieldById,
  };
};
const mapDispatch = ({ space }: IDispatch) => ({
  updateSpace: (payload: any) => space.updateSpace(payload),
});

export default connect(
  mapState,
  // @ts-ignore
  mapDispatch,
)(SpaceEdit);
